/* tslint:disable */
/* eslint-disable */
/**
 * Scrivio Certificates API
 * Scrivio API for certificate management. Manages PKCS#12 certificates for external integrations (UZI, Zorgmail).  ## Authentication All endpoints require Firebase Authentication JWT token in `Authorization: Bearer <token>` header.  ## Error Responses All error responses follow RFC 7807 Problem Details format. 
 *
 * The version of the OpenAPI document: 1.0.0
 * Contact: support@scrivio.nl
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import type { Configuration } from './configuration';
import type { AxiosPromise, AxiosInstance, RawAxiosRequestConfig } from 'axios';
import globalAxios from 'axios';
// Some imports not used depending on template conditions
// @ts-ignore
import { DUMMY_BASE_URL, assertParamExists, setApiKeyToObject, setBasicAuthToObject, setBearerAuthToObject, setOAuthToObject, setSearchParams, serializeDataIfNeeded, toPathString, createRequestFunction } from './common';
import type { RequestArgs } from './base';
// @ts-ignore
import { BASE_PATH, COLLECTION_FORMATS, BaseAPI, RequiredError, operationServerMap } from './base';

/**
 * Certificate details
 */
export interface CertificateDto {
    /**
     * Unique certificate identifier
     */
    'id': string;
    'type': CertificateType;
    'status': CertificateStatus;
    /**
     * SHA-256 fingerprint of the certificate
     */
    'fingerprint': string;
    /**
     * Subject Distinguished Name from the certificate
     */
    'subjectDn': string;
    /**
     * Certificate validity start date (ISO 8601)
     */
    'validFrom': string;
    /**
     * Certificate validity end date (ISO 8601)
     */
    'validUntil': string;
    /**
     * ID of the user who uploaded the certificate
     */
    'uploadedBy': string;
    /**
     * When the certificate was uploaded (ISO 8601)
     */
    'uploadedAt': string;
    /**
     * When the certificate was last tested (ISO 8601)
     */
    'lastTestedAt'?: string | null;
    /**
     * Whether the last test was successful
     */
    'lastTestSuccess'?: boolean | null;
}


/**
 * List of certificates
 */
export interface CertificateListResponse {
    'items': Array<CertificateDto>;
}
/**
 * Current status of the certificate: - ACTIVE: Certificate is valid and in use - EXPIRING_SOON: Certificate expires within 30 days - EXPIRED: Certificate has expired - REVOKED: Certificate has been revoked 
 */

export const CertificateStatus = {
    Active: 'ACTIVE',
    ExpiringSoon: 'EXPIRING_SOON',
    Expired: 'EXPIRED',
    Revoked: 'REVOKED'
} as const;

export type CertificateStatus = typeof CertificateStatus[keyof typeof CertificateStatus];


/**
 * Result of a certificate connection test
 */
export interface CertificateTestResultDto {
    /**
     * Whether the test connection was successful
     */
    'success': boolean;
    /**
     * When the test was performed (ISO 8601)
     */
    'testedAt': string;
    /**
     * Error message if the test failed
     */
    'errorMessage'?: string | null;
    /**
     * The endpoint that was tested
     */
    'endpoint': string;
}
/**
 * Type of certificate for external integrations: - UZI_SERVER: UZI server certificate for SBV-Z and other healthcare integrations - ZORGMAIL: Zorgmail certificate for secure healthcare communication 
 */

export const CertificateType = {
    UziServer: 'UZI_SERVER',
    Zorgmail: 'ZORGMAIL'
} as const;

export type CertificateType = typeof CertificateType[keyof typeof CertificateType];


export interface ProblemDetail {
    /**
     * A URI reference that identifies the problem type
     */
    'type': string;
    /**
     * A short, human-readable summary of the problem
     */
    'title': string;
    /**
     * The HTTP status code
     */
    'status': number;
    /**
     * A human-readable explanation specific to this occurrence
     */
    'detail'?: string;
    /**
     * A URI reference that identifies the specific occurrence
     */
    'instance'?: string;
    /**
     * List of validation errors
     */
    'errors'?: Array<ProblemDetailErrorsInner>;
}
export interface ProblemDetailErrorsInner {
    /**
     * Field name that failed validation
     */
    'field'?: string;
    /**
     * Error message
     */
    'message'?: string;
}

/**
 * CertificatesApi - axios parameter creator
 */
export const CertificatesApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Deletes a certificate from the organization.  Requires `integrations.manage` permission. 
         * @summary Delete certificate
         * @param {string} organizationId Organization ID
         * @param {string} certificateId Certificate ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteCertificate: async (organizationId: string, certificateId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('deleteCertificate', 'organizationId', organizationId)
            // verify required parameter 'certificateId' is not null or undefined
            assertParamExists('deleteCertificate', 'certificateId', certificateId)
            const localVarPath = `/api/v1/organizations/{organizationId}/certificates/{certificateId}`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)))
                .replace(`{${"certificateId"}}`, encodeURIComponent(String(certificateId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns all certificates for the organization. Requires `integrations.manage` permission. 
         * @summary List certificates
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listCertificates: async (organizationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('listCertificates', 'organizationId', organizationId)
            const localVarPath = `/api/v1/organizations/{organizationId}/certificates`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Replaces an existing certificate with a new one. The old certificate is archived and a new certificate is created.  File size limit: 50KB.  Requires `integrations.manage` permission. 
         * @summary Replace certificate
         * @param {string} organizationId Organization ID
         * @param {string} certificateId Certificate ID
         * @param {File} file PKCS#12 certificate file (.p12 or .pfx)
         * @param {string} passphrase Passphrase to decrypt the certificate
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        replaceCertificate: async (organizationId: string, certificateId: string, file: File, passphrase: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('replaceCertificate', 'organizationId', organizationId)
            // verify required parameter 'certificateId' is not null or undefined
            assertParamExists('replaceCertificate', 'certificateId', certificateId)
            // verify required parameter 'file' is not null or undefined
            assertParamExists('replaceCertificate', 'file', file)
            // verify required parameter 'passphrase' is not null or undefined
            assertParamExists('replaceCertificate', 'passphrase', passphrase)
            const localVarPath = `/api/v1/organizations/{organizationId}/certificates/{certificateId}`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)))
                .replace(`{${"certificateId"}}`, encodeURIComponent(String(certificateId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;
            const localVarFormParams = new ((configuration && configuration.formDataCtor) || FormData)();

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


            if (file !== undefined) { 
                localVarFormParams.append('file', file as any);
            }

            if (passphrase !== undefined) { 
                localVarFormParams.append('passphrase', passphrase as any);
            }
            localVarHeaderParameter['Content-Type'] = 'multipart/form-data';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = localVarFormParams;

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Tests the certificate by attempting to connect to the appropriate endpoint based on the certificate type (UZI or Zorgmail).  Requires `integrations.manage` permission. 
         * @summary Test certificate connection
         * @param {string} organizationId Organization ID
         * @param {string} certificateId Certificate ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        testCertificate: async (organizationId: string, certificateId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('testCertificate', 'organizationId', organizationId)
            // verify required parameter 'certificateId' is not null or undefined
            assertParamExists('testCertificate', 'certificateId', certificateId)
            const localVarPath = `/api/v1/organizations/{organizationId}/certificates/{certificateId}/test`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)))
                .replace(`{${"certificateId"}}`, encodeURIComponent(String(certificateId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Uploads a new PKCS#12 certificate for the organization. The certificate is validated for format, passphrase, and expiration.  File size limit: 50KB.  Requires `integrations.manage` permission. 
         * @summary Upload certificate
         * @param {string} organizationId Organization ID
         * @param {File} file PKCS#12 certificate file (.p12 or .pfx)
         * @param {string} passphrase Passphrase to decrypt the certificate
         * @param {CertificateType} type 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        uploadCertificate: async (organizationId: string, file: File, passphrase: string, type: CertificateType, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('uploadCertificate', 'organizationId', organizationId)
            // verify required parameter 'file' is not null or undefined
            assertParamExists('uploadCertificate', 'file', file)
            // verify required parameter 'passphrase' is not null or undefined
            assertParamExists('uploadCertificate', 'passphrase', passphrase)
            // verify required parameter 'type' is not null or undefined
            assertParamExists('uploadCertificate', 'type', type)
            const localVarPath = `/api/v1/organizations/{organizationId}/certificates`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;
            const localVarFormParams = new ((configuration && configuration.formDataCtor) || FormData)();

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


            if (file !== undefined) { 
                localVarFormParams.append('file', file as any);
            }

            if (passphrase !== undefined) { 
                localVarFormParams.append('passphrase', passphrase as any);
            }

            if (type !== undefined) { 
                localVarFormParams.append('type', type as any);
            }
            localVarHeaderParameter['Content-Type'] = 'multipart/form-data';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = localVarFormParams;

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * CertificatesApi - functional programming interface
 */
export const CertificatesApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = CertificatesApiAxiosParamCreator(configuration)
    return {
        /**
         * Deletes a certificate from the organization.  Requires `integrations.manage` permission. 
         * @summary Delete certificate
         * @param {string} organizationId Organization ID
         * @param {string} certificateId Certificate ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteCertificate(organizationId: string, certificateId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteCertificate(organizationId, certificateId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['CertificatesApi.deleteCertificate']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns all certificates for the organization. Requires `integrations.manage` permission. 
         * @summary List certificates
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listCertificates(organizationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CertificateListResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listCertificates(organizationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['CertificatesApi.listCertificates']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Replaces an existing certificate with a new one. The old certificate is archived and a new certificate is created.  File size limit: 50KB.  Requires `integrations.manage` permission. 
         * @summary Replace certificate
         * @param {string} organizationId Organization ID
         * @param {string} certificateId Certificate ID
         * @param {File} file PKCS#12 certificate file (.p12 or .pfx)
         * @param {string} passphrase Passphrase to decrypt the certificate
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async replaceCertificate(organizationId: string, certificateId: string, file: File, passphrase: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CertificateDto>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.replaceCertificate(organizationId, certificateId, file, passphrase, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['CertificatesApi.replaceCertificate']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Tests the certificate by attempting to connect to the appropriate endpoint based on the certificate type (UZI or Zorgmail).  Requires `integrations.manage` permission. 
         * @summary Test certificate connection
         * @param {string} organizationId Organization ID
         * @param {string} certificateId Certificate ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async testCertificate(organizationId: string, certificateId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CertificateTestResultDto>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.testCertificate(organizationId, certificateId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['CertificatesApi.testCertificate']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Uploads a new PKCS#12 certificate for the organization. The certificate is validated for format, passphrase, and expiration.  File size limit: 50KB.  Requires `integrations.manage` permission. 
         * @summary Upload certificate
         * @param {string} organizationId Organization ID
         * @param {File} file PKCS#12 certificate file (.p12 or .pfx)
         * @param {string} passphrase Passphrase to decrypt the certificate
         * @param {CertificateType} type 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async uploadCertificate(organizationId: string, file: File, passphrase: string, type: CertificateType, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CertificateDto>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.uploadCertificate(organizationId, file, passphrase, type, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['CertificatesApi.uploadCertificate']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * CertificatesApi - factory interface
 */
export const CertificatesApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = CertificatesApiFp(configuration)
    return {
        /**
         * Deletes a certificate from the organization.  Requires `integrations.manage` permission. 
         * @summary Delete certificate
         * @param {string} organizationId Organization ID
         * @param {string} certificateId Certificate ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteCertificate(organizationId: string, certificateId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteCertificate(organizationId, certificateId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns all certificates for the organization. Requires `integrations.manage` permission. 
         * @summary List certificates
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listCertificates(organizationId: string, options?: RawAxiosRequestConfig): AxiosPromise<CertificateListResponse> {
            return localVarFp.listCertificates(organizationId, options).then((request) => request(axios, basePath));
        },
        /**
         * Replaces an existing certificate with a new one. The old certificate is archived and a new certificate is created.  File size limit: 50KB.  Requires `integrations.manage` permission. 
         * @summary Replace certificate
         * @param {string} organizationId Organization ID
         * @param {string} certificateId Certificate ID
         * @param {File} file PKCS#12 certificate file (.p12 or .pfx)
         * @param {string} passphrase Passphrase to decrypt the certificate
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        replaceCertificate(organizationId: string, certificateId: string, file: File, passphrase: string, options?: RawAxiosRequestConfig): AxiosPromise<CertificateDto> {
            return localVarFp.replaceCertificate(organizationId, certificateId, file, passphrase, options).then((request) => request(axios, basePath));
        },
        /**
         * Tests the certificate by attempting to connect to the appropriate endpoint based on the certificate type (UZI or Zorgmail).  Requires `integrations.manage` permission. 
         * @summary Test certificate connection
         * @param {string} organizationId Organization ID
         * @param {string} certificateId Certificate ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        testCertificate(organizationId: string, certificateId: string, options?: RawAxiosRequestConfig): AxiosPromise<CertificateTestResultDto> {
            return localVarFp.testCertificate(organizationId, certificateId, options).then((request) => request(axios, basePath));
        },
        /**
         * Uploads a new PKCS#12 certificate for the organization. The certificate is validated for format, passphrase, and expiration.  File size limit: 50KB.  Requires `integrations.manage` permission. 
         * @summary Upload certificate
         * @param {string} organizationId Organization ID
         * @param {File} file PKCS#12 certificate file (.p12 or .pfx)
         * @param {string} passphrase Passphrase to decrypt the certificate
         * @param {CertificateType} type 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        uploadCertificate(organizationId: string, file: File, passphrase: string, type: CertificateType, options?: RawAxiosRequestConfig): AxiosPromise<CertificateDto> {
            return localVarFp.uploadCertificate(organizationId, file, passphrase, type, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * CertificatesApi - object-oriented interface
 */
export class CertificatesApi extends BaseAPI {
    /**
     * Deletes a certificate from the organization.  Requires `integrations.manage` permission. 
     * @summary Delete certificate
     * @param {string} organizationId Organization ID
     * @param {string} certificateId Certificate ID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteCertificate(organizationId: string, certificateId: string, options?: RawAxiosRequestConfig) {
        return CertificatesApiFp(this.configuration).deleteCertificate(organizationId, certificateId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns all certificates for the organization. Requires `integrations.manage` permission. 
     * @summary List certificates
     * @param {string} organizationId Organization ID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listCertificates(organizationId: string, options?: RawAxiosRequestConfig) {
        return CertificatesApiFp(this.configuration).listCertificates(organizationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Replaces an existing certificate with a new one. The old certificate is archived and a new certificate is created.  File size limit: 50KB.  Requires `integrations.manage` permission. 
     * @summary Replace certificate
     * @param {string} organizationId Organization ID
     * @param {string} certificateId Certificate ID
     * @param {File} file PKCS#12 certificate file (.p12 or .pfx)
     * @param {string} passphrase Passphrase to decrypt the certificate
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public replaceCertificate(organizationId: string, certificateId: string, file: File, passphrase: string, options?: RawAxiosRequestConfig) {
        return CertificatesApiFp(this.configuration).replaceCertificate(organizationId, certificateId, file, passphrase, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Tests the certificate by attempting to connect to the appropriate endpoint based on the certificate type (UZI or Zorgmail).  Requires `integrations.manage` permission. 
     * @summary Test certificate connection
     * @param {string} organizationId Organization ID
     * @param {string} certificateId Certificate ID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public testCertificate(organizationId: string, certificateId: string, options?: RawAxiosRequestConfig) {
        return CertificatesApiFp(this.configuration).testCertificate(organizationId, certificateId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Uploads a new PKCS#12 certificate for the organization. The certificate is validated for format, passphrase, and expiration.  File size limit: 50KB.  Requires `integrations.manage` permission. 
     * @summary Upload certificate
     * @param {string} organizationId Organization ID
     * @param {File} file PKCS#12 certificate file (.p12 or .pfx)
     * @param {string} passphrase Passphrase to decrypt the certificate
     * @param {CertificateType} type 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public uploadCertificate(organizationId: string, file: File, passphrase: string, type: CertificateType, options?: RawAxiosRequestConfig) {
        return CertificatesApiFp(this.configuration).uploadCertificate(organizationId, file, passphrase, type, options).then((request) => request(this.axios, this.basePath));
    }
}



